/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, ImageStyle, useWindowDimensions, ViewStyle } from "react-native"
import {
  AutoImage,
  Button,
  FlatList,
  JalaLoading,
  LazyRender,
  ListEmptyProps,
  ListRenderItem,
  ListRenderItemInfo,
  Screen,
  Text,
} from "../../../components"
import { CompositeScreenProps, useFocusEffect } from "@react-navigation/native"
import { Skeleton, View } from "native-base"
import Icon from 'react-native-vector-icons/MaterialIcons'
import { useStores } from "../../../models"
import { navigate } from "../../../navigators"
import { color } from "../../../theme"
import { SafeAreaView } from "react-native-safe-area-context"
import JalaAnalytic from "../../../utils/jala-analytic"

const IMAGE = require("./empty-farm.png");


const ROOT: ViewStyle = {
  flex: 1,
  paddingHorizontal: 16,
}

const LIST_CONTAINER: ViewStyle = {
  borderRadius: 10,
  borderWidth: 1,
  borderColor: color.palette.gray200,
  backgroundColor: color.palette.white,
  paddingHorizontal: 12,
  paddingVertical: 12,
  marginVertical: 6,
  alignItems: "stretch",
  elevation: 1
}

const IMG: ImageStyle = {
  resizeMode: 'contain',
  height: 150,
  marginBottom: 20
}

const CONTENT_CONTAINER: ViewStyle = {
  paddingTop: 12,
  paddingBottom: 12
}

const EmptyState: FC<{colorScheme?: "light" | "dark", height?: string}> = (props: ListEmptyProps) => {
  const { colorScheme = Appearance.getColorScheme(), ...restProps } = props;
  return (
  <View flexGrow={1} alignItems="center" justifyContent="center" {...restProps}>
    <AutoImage source={IMAGE} style={IMG} />
    <Text preset="title4" tx="connectedFarm.emptyTitle" colorScheme={colorScheme} />
    <Text preset="title4" tx="connectedFarm.emptySubtitle" textAlign="center" mt="4px" colorScheme={colorScheme} />
  </View>
)}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, handleNavigate?: (item: any) => void }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info, handleNavigate } = props;
  const { item, item: { farm_id, farm: { name, area, region }, role: { display_name } } } = info;

  const { userStore: { connectedFarm } } = useStores();

  const members = useMemo(() => (connectedFarm || []).filter((item) => item.farm_id === farm_id), [connectedFarm, farm_id])
  const nullArea = useMemo(() => area === null ? "- m2" : `${area} m2`, [area])

  const regionName = region && region.full_name || "-";
  const member = members && members.length;

  const farm = useMemo(() => {
    const selectedFarm = { farmId: item.farm.id, farmName: name, regionName, area, member: members };
    return {
      data: item,
      farmName: name,
      region: regionName,
      area: nullArea,
      member: members,
      selectedFarm
    }
  },[area, item, members, name, nullArea, regionName])


  const onNavigate = useCallback(() => handleNavigate(farm), [farm, handleNavigate])

  const renderPlaceholder = useCallback(() => {
    return <View style={LIST_CONTAINER}>
        <Skeleton height="20px" mb="6px" />
        <Skeleton height="18px" mb="20px" />
        <Skeleton height="16px" mb="6px" />
        <Skeleton height="20px" mb="6px" />
    </View>
  }, [])


  return <LazyRender renderPlaceholder={renderPlaceholder}>
    <Button testID="jala:id/item-button" preset="link" colorScheme={colorScheme} style={LIST_CONTAINER} onPress={onNavigate}>
      <Text preset="display2" color={color.primaryDarker} text={name} numberOfLines={1} isTruncated marginBottom="4px" colorScheme={colorScheme} />
      <View flexDirection="row">
        <Icon name="place" size={15} color={color.palette.gray800} />
        <Text preset="body3" numberOfLines={1} isTruncated text={regionName} ml="6px" textTransform="uppercase" color={color.primary} colorScheme={colorScheme} />
      </View>
      <View flexDirection="row" mt="8px">
        <View flex={1}>
          <Text preset="caption1" tx="connectedFarm.roleId" marginBottom="4px" colorScheme={colorScheme} />
          <Text preset="title4" text={display_name} color={color.primaryDarker} colorScheme={colorScheme} />
        </View>
        <View width="8px" />
        <View flex={1}>
          <Text preset="caption1" tx="connectedFarm.member" marginBottom="4px" colorScheme={colorScheme} />
          <Text preset="title4" color={color.primaryDarker} colorScheme={colorScheme}>{member}</Text>
        </View>
      </View>
    </Button>
  </LazyRender>;
})

export const ConnectedFarmScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function ConnectedFarmScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const { userStore, userStore: { connectedFarm, user, loading: loadingModel }, farmStore, farmStore: { needRefresh } } = useStores();

  const data = useMemo(() => {
    return (!loadingModel && (connectedFarm || []).filter((farm) => farm.user_id === user.id) || [])
  }, [connectedFarm, loadingModel, user.id])

  useEffect(() => {
    userStore.getFarmProfile();
  }, [userStore])

  useFocusEffect(useCallback(() => {
    if (needRefresh) {
      userStore.getFarmProfile();
      farmStore.set('needRefresh', false);
      farmStore.set('pushIdQueue', {});
    }
  }, [farmStore, needRefresh, userStore]))

  useEffect(() => {
    JalaAnalytic.log('Mobile app: connected farm');
  }, []);

  const handleNavigate = useCallback((item) => {
    navigate("Profile.Stack", 'Profile.Connected.Farm.Member', {...item})
  }, [])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} handleNavigate={handleNavigate} colorScheme={colorScheme} />
  }, [colorScheme, handleNavigate])

  const keyExtractor = useCallback((item, index) => `${item.id || index}`, [])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundColor={color.palette.backgroundGray}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
      hideOnBlurred={false}
    >
      <FlatList
        key={(connectedFarm || []).map(item => item.farm_id).join(",")}
        data={data}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        contentContainerStyle={CONTENT_CONTAINER}
        ListEmptyComponent={loadingModel && (
          <View flexGrow={1} alignItems="center" justifyContent="center" height={`${height - 90}px`}>
            <JalaLoading />
          </View>
        ) || <EmptyState colorScheme={colorScheme} height={`${height - 100}px`} />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        refreshing={false}
        estimatedItemSize={144}
        showsVerticalScrollIndicator={false}
      />
    </Screen>
  )
})
